"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { AccountBadge, Page, Pill } from "@/components/ui";
import { Account, api } from "@/lib/api";
import { fmtRupeesShort, fmtDate } from "@/lib/fmt";

export default function AccountsHome() {
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.accounts().then(setAccounts).catch((e) => setErr(String(e)));
  }, []);

  if (err) return <p className="text-sm text-down">{err}</p>;
  if (accounts === null) return <p className="eyebrow text-mute">Loading…</p>;
  if (accounts.length === 0) {
    return (
      <Page eyebrow="Index" title={<><em className="italic">No</em> accounts.</>}>
        <p className="max-w-prose text-sm text-mute">
          Your user has zero accounts mapped. Ask an admin to grant
          access via the <code>dashboard_user_account</code> table.
          {/* No /admin link here — only non-admins reach this state
              (admins get every active account by default via
              allowed_account_ids), and non-admins can't open /admin
              anyway (auth.py AdminAuth rejects them). */}
        </p>
      </Page>
    );
  }

  return (
    <Page
      eyebrow={`Index · ${accounts.length} account${accounts.length === 1 ? "" : "s"}`}
      title={<><em className="italic">Accounts</em></>}
    >
      <ul className="divide-y divide-rule border-y border-rule">
        {accounts.map((a, i) => (
          <li key={a.account_id}>
            <Link
              href={`/accounts/${a.account_id}`}
              className={`group grid grid-cols-12 items-baseline gap-6 py-8 transition hover:bg-white/[0.02] ${
                a.active ? "" : "opacity-55"
              }`}
            >
              <div className="col-span-1 font-mono text-xs text-mute">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="col-span-12 sm:col-span-5">
                <div className="display text-3xl sm:text-4xl text-foreground transition group-hover:text-accent">
                  {a.name}
                </div>
                <div className="eyebrow mt-2">{a.account}</div>
              </div>
              <div className="col-span-6 sm:col-span-3 flex flex-wrap items-center gap-2">
                <Pill variant="outline">{a.broker}</Pill>
                <AccountBadge
                  is_client_account={a.is_client_account}
                  clientcode={a.clientcode} accountId={a.account_id}
                />
                {!a.active && <Pill variant="down">Inactive</Pill>}
              </div>
              <div className="col-span-6 sm:col-span-3 text-right">
                {/* Margin is shown for active accounts only; inactive → blank (#198). */}
                {a.active && (
                  <>
                    <div className="text-base tabular-nums text-foreground">
                      {fmtRupeesShort(a.margin_in_force_rs)}
                    </div>
                    <div className="eyebrow mt-1 text-mute">
                      {a.margin_effective_date
                        ? `margin as of ${fmtDate(a.margin_effective_date)}`
                        : "no margin"}
                    </div>
                  </>
                )}
                <span className="eyebrow mt-2 inline-block transition group-hover:text-foreground">
                  Open →
                </span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </Page>
  );
}
